import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Lock, Save, FileText, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import '../styles/Admin.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTENT_SECTIONS = [
  {
    section: 'hero',
    name: 'Hero Section',
    fields: [
      { key: 'headline', label: 'Headline', type: 'text' },
      { key: 'subheadline', label: 'Subheadline', type: 'text' },
      { key: 'intro_text', label: 'Introduction Text', type: 'textarea' }
    ]
  },
  {
    section: 'philosophy',
    name: 'Philosophy / About HGV',
    fields: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'paragraph_1', label: 'Paragraph 1', type: 'textarea' },
      { key: 'paragraph_2', label: 'Paragraph 2', type: 'textarea' },
      { key: 'paragraph_3', label: 'Paragraph 3', type: 'textarea' }
    ]
  },
  {
    section: 'about_kristen',
    name: 'About Kristen',
    fields: [
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'bio_paragraph_1', label: 'Bio Paragraph 1', type: 'textarea' },
      { key: 'bio_paragraph_2', label: 'Bio Paragraph 2', type: 'textarea' },
      { key: 'bio_paragraph_3', label: 'Bio Paragraph 3', type: 'textarea' },
      { key: 'press_heading', label: 'Press Section Heading', type: 'text' }
    ]
  },
  {
    section: 'tarot',
    name: 'Tarot for Bros™',
    fields: [
      { key: 'badge', label: 'Badge Text', type: 'text' },
      { key: 'heading', label: 'Heading', type: 'text' },
      { key: 'tagline', label: 'Tagline', type: 'text' },
      { key: 'waitlist_text', label: 'Waitlist Text', type: 'text' },
      { key: 'description_paragraph_1', label: 'Description Paragraph 1', type: 'textarea' },
      { key: 'description_paragraph_2', label: 'Description Paragraph 2', type: 'textarea' }
    ]
  },
  {
    section: 'past_projects',
    name: 'Past Projects',
    fields: [
      { key: 'heading', label: 'Section Heading', type: 'text' },
      { key: 'eden_forest_title', label: 'Eden Forest - Title', type: 'text' },
      { key: 'eden_forest_description', label: 'Eden Forest - Description', type: 'textarea' },
      { key: 'farm_school_title', label: 'Farm School - Title', type: 'text' },
      { key: 'farm_school_description', label: 'Farm School - Description', type: 'textarea' },
      { key: 'culinary_creators_title', label: 'Culinary Creators - Title', type: 'text' },
      { key: 'culinary_creators_description', label: 'Culinary Creators - Description', type: 'textarea' },
      { key: 'hungry_house_title', label: 'Hungry House - Title', type: 'text' },
      { key: 'hungry_house_description', label: 'Hungry House - Description', type: 'textarea' }
    ]
  },
  {
    section: 'work_with_kristen',
    name: 'Work With Kristen',
    fields: [
      { key: 'heading', label: 'Section Heading', type: 'text' },
      { key: 'mastermind_title', label: 'Business Mastermind - Title', type: 'text' },
      { key: 'mastermind_description', label: 'Business Mastermind - Description', type: 'textarea' },
      { key: 'tarot_readings_title', label: 'Tarot Readings - Title', type: 'text' },
      { key: 'tarot_readings_description', label: 'Tarot Readings - Description', type: 'textarea' }
    ]
  },
  {
    section: 'footer',
    name: 'Footer',
    fields: [
      { key: 'heading', label: 'Newsletter Heading', type: 'text' },
      { key: 'description', label: 'Newsletter Description', type: 'text' },
      { key: 'copyright', label: 'Copyright Text', type: 'text' }
    ]
  }
];

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('content');
  const [savingSection, setSavingSection] = useState(null);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/auth`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        localStorage.setItem('admin_auth', 'true');
        loadContent();
        showMessage('success', 'Login successful!');
      }
    } catch (error) {
      showMessage('error', 'Invalid password');
    }
  };

  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/content`);
      const contentMap = {};
      response.data.forEach(item => {
        if (!contentMap[item.section]) {
          contentMap[item.section] = {};
        }
        contentMap[item.section][item.key] = item.value;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error loading content:', error);
      showMessage('error', 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (section, key, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const saveSection = async (section) => {
    setSavingSection(section);
    try {
      const sectionContent = content[section] || {};
      const updates = Object.entries(sectionContent).map(([key, value]) => ({
        section,
        key,
        value
      }));

      await Promise.all(
        updates.map(update => axios.post(`${API}/admin/content`, update))
      );

      showMessage('success', `${CONTENT_SECTIONS.find(s => s.section === section)?.name} saved successfully!`);
    } catch (error) {
      console.error('Error saving content:', error);
      showMessage('error', 'Failed to save content');
    } finally {
      setSavingSection(null);
    }
  };

  useEffect(() => {
    const auth = localStorage.getItem('admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      loadContent();
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-card">
          <Lock size={48} className="login-icon" />
          <h1>Admin Panel</h1>
          <p>Highest Good Ventures Content Management</p>
          
          <form onSubmit={handleLogin} className="login-form">
            <Input
              type="password"
              placeholder="Enter admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="password-input"
            />
            <Button type="submit" className="login-btn">
              Login
            </Button>
          </form>
          
          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          <p className="default-password">Default password: admin123</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div>
          <h1>Content Management System</h1>
          <p>Manage all website text and images</p>
        </div>
        <Button 
          variant="outline"
          onClick={() => {
            setIsAuthenticated(false);
            localStorage.removeItem('admin_auth');
          }}
        >
          Logout
        </Button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          <FileText size={18} />
          Text Content
        </button>
        <button
          className={`tab ${activeTab === 'images' ? 'active' : ''}`}
          onClick={() => setActiveTab('images')}
        >
          <ImageIcon size={18} />
          Images (Coming Soon)
        </button>
      </div>

      {activeTab === 'content' && (
        <div className="content-sections">
          {CONTENT_SECTIONS.map(section => (
            <Card key={section.section} className="content-section-card">
              <div className="section-header">
                <h2>{section.name}</h2>
                <Button
                  onClick={() => saveSection(section.section)}
                  disabled={savingSection === section.section}
                  className="save-btn"
                >
                  <Save size={16} />
                  {savingSection === section.section ? 'Saving...' : 'Save Section'}
                </Button>
              </div>

              <div className="section-fields">
                {section.fields.map(field => (
                  <div key={field.key} className="field-group">
                    <label className="field-label">{field.label}</label>
                    {field.type === 'textarea' ? (
                      <textarea
                        className="field-textarea"
                        value={content[section.section]?.[field.key] || ''}
                        onChange={(e) => handleContentChange(section.section, field.key, e.target.value)}
                        rows={4}
                      />
                    ) : (
                      <Input
                        type="text"
                        className="field-input"
                        value={content[section.section]?.[field.key] || ''}
                        onChange={(e) => handleContentChange(section.section, field.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'images' && (
        <div className="images-placeholder">
          <ImageIcon size={64} />
          <h3>Image Management Coming Soon</h3>
          <p>For now, images can be updated directly in the code or contact support.</p>
        </div>
      )}

      <div className="admin-footer">
        <p>💡 <strong>Tip:</strong> Remember to click "Save Section" after making changes to each section.</p>
        <p>🔒 Change your password by updating ADMIN_PASSWORD_HASH in backend/.env</p>
      </div>
    </div>
  );
};

export default Admin;
